import { AlertTriangle, RotateCw } from 'lucide-react';
import type { ReactNode } from 'react';
import { NavLink } from 'react-router';
import { errorCode, errorMessage } from '@/api/query';
import { Badge, Button, Card, Skeleton } from '@/components/ui';
import { cn } from '@/lib/cn';
import { STATUS_LABELS, type TxStatus } from './labels';
import { BRAND } from '@/brand';

/** Table-shaped loading placeholder (reserves the layout so nothing jumps). */
export const TableSkeleton = ({ rows = 8, className }: { readonly rows?: number; readonly className?: string }) => (
  <div className={cn('rounded-lg border border-border bg-surface p-4', className)} role="status" aria-label="Loading">
    <Skeleton className="mb-4 h-5 w-48" />
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} className="flex items-center gap-4 border-b border-border py-3 last:border-0">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 flex-1" />
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-20" />
      </div>
    ))}
  </div>
);

/** Card-grid loading placeholder. */
export const CardsSkeleton = ({ count = 6, className }: { readonly count?: number; readonly className?: string }) => (
  <div className={cn('grid gap-4 sm:grid-cols-2 xl:grid-cols-3', className)} role="status" aria-label="Loading">
    {Array.from({ length: count }, (_, i) => (
      <Skeleton key={i} className="h-44 rounded-lg" />
    ))}
  </div>
);

export interface QueryErrorProps {
  readonly error: unknown;
  readonly onRetry?: () => void;
  readonly title?: string;
  /** Extra guidance (e.g. what still works). */
  readonly children?: ReactNode;
  readonly className?: string;
}

/** Inline error panel that says what failed and offers a retry. NOT_FOUND procedures read as "not available yet". */
export const QueryError = ({ error, onRetry, title = "This couldn't be loaded", children, className }: QueryErrorProps) => {
  const missing = errorCode(error) === 'NOT_FOUND' && /No (fake|procedure)|No ".*" procedure/i.test(errorMessage(error));
  return (
    <Card className={cn('flex flex-col items-start gap-3 p-5 sm:flex-row', className)} role="alert">
      <AlertTriangle className="mt-0.5 size-5 shrink-0 text-warning" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="font-medium text-text">{missing ? `This part of ${BRAND.name} is not available on this server yet` : title}</p>
        <p className="mt-1 text-sm text-text-muted">{missing ? 'Everything else keeps working. Update the server to use it.' : errorMessage(error)}</p>
        {children}
      </div>
      {onRetry ? (
        <Button variant="outline" size="sm" icon={<RotateCw aria-hidden />} onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </Card>
  );
};

const STATUS_TONE: Readonly<Record<TxStatus, 'neutral' | 'info' | 'positive' | 'warning'>> = {
  pending: 'warning', cleared: 'info', reconciled: 'positive', void: 'neutral',
};

/** Transaction status as a text chip (colour is secondary to the word). */
export const StatusChip = ({ status, className }: { readonly status: TxStatus; readonly className?: string }) => (
  <Badge tone={STATUS_TONE[status]} className={cn(status === 'void' && 'line-through', className)}>
    {STATUS_LABELS[status]}
  </Badge>
);

export interface SectionLink {
  readonly to: string;
  readonly label: string;
  /** Match only the exact path (for the area's index tab). */
  readonly end?: boolean;
}

/** Secondary navigation between an area's sub-pages, styled like tabs; scrolls horizontally on small screens. */
export const SectionNav = ({ links, label }: { readonly links: readonly SectionLink[]; readonly label: string }) => (
  <nav aria-label={label} className="-mx-4 mb-6 overflow-x-auto px-4 sm:mx-0 sm:px-0">
    <ul className="inline-flex min-w-max items-center gap-1 rounded-lg bg-surface-sunken p-1">
      {links.map((l) => (
        <li key={l.to}>
          <NavLink
            to={l.to}
            end={l.end}
            className={({ isActive }) =>
              cn(
                'inline-flex h-9 items-center rounded-md px-3 text-sm font-medium text-text-muted transition-colors duration-fast hover:text-text coarse:h-11',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                isActive && 'bg-surface text-text shadow-sm',
              )
            }
          >
            {l.label}
          </NavLink>
        </li>
      ))}
    </ul>
  </nav>
);
